import React from "react"
import {
  styled,
  forwardRef,
  createStyles,
  StylePropType,
} from "@design-system/core"
import { __DEV__ } from "@design-system/utils"
import { useInteractive } from "@design-system/interactive"
import { TextButtonProps, TextButtonVariants } from "./types"

export const TextButton = forwardRef<TextButtonProps, "div">((props, ref) => {
  const {
    size,
    icon,
    iconPosition,
    children,
    style,
    iconStyle,
    variant,
    ...otherProps
  } = props
  const {
    isDisabled,
    isHovered,
    isPressed,
    isFocused,
    ...interactiveProps
  } = useInteractive(otherProps)
  const variantStyle = variant ? variantStyles[variant] : undefined

  const iconElement = icon
    ? React.cloneElement(React.Children.only(icon), {
        style: [
          styles.icon,
          iconPosition === "right" ? styles.iconRight : styles.iconLeft,
          variantStyle?.default.icon,
          isHovered && [
            stateStyles.hover.icon,
            variantStyle?.states.hover.icon,
          ],
          size && [sizeStyles[size]?.icon, variantStyle?.sizes[size]?.icon],
          icon.props?.style,
          iconStyle,
        ],
      })
    : null

  return (
    <styled.div
      tabIndex={0}
      role="button"
      data-focusable="true"
      {...interactiveProps}
      ref={ref}
      style={
        [
          styles.container,
          variantStyle?.default.container,
          size && [
            sizeStyles[size]?.container,
            variantStyle?.sizes[size]?.container,
          ],
          isHovered && [
            stateStyles.hover.container,
            variantStyle?.states.hover.container,
          ],
          style,
        ] as StylePropType
      }
    >
      {iconPosition === "right" ? null : iconElement}
      {children}
      {iconPosition === "right" ? iconElement : null}
    </styled.div>
  )
})

if (__DEV__) {
  TextButton.displayName = "TextButton"
}

const styles = createStyles((theme) => ({
  container: {
    alignItems: "center",
    justifyContent: "center",
    display: "inline-flex",
    flexDirection: "row",
    cursor: "pointer",
    whiteSpace: "nowrap",
    userSelect: "none",
    padding: 0,
    ...theme.textButton.base.default.container,
  },
  icon: theme.textButton.base.default.icon,
  iconLeft: {
    marginLeft: 0,
  },
  iconRight: {
    marginRight: 0,
  },
}))

const sizeStyles = {
  small: createStyles((theme) => theme.textButton.base.sizes.small || {}),
  medium: createStyles((theme) => theme.textButton.base.sizes.medium || {}),
}

const stateStyles = {
  hover: createStyles((theme) => theme.textButton.base.states.hover || {}),
}

const createVariantStyles = (name: TextButtonVariants) => ({
  default: createStyles((theme) => theme.textButton.variants[name]?.default),
  sizes: {
    small: createStyles(
      (theme) => theme.textButton.variants[name]?.sizes.small || {},
    ),
    medium: createStyles(
      (theme) => theme.textButton.variants[name]?.sizes.medium || {},
    ),
  },
  states: {
    hover: createStyles(
      (theme) => theme.textButton.variants[name]?.states.hover || {},
    ),
  },
})

const variantStyles = {
  inverse: createVariantStyles("inverse"),
}
